import { ForteXApi } from "@fortex/api";
import type { ForteXApiError } from "@fortex/api";
import type { Theme } from "@fortex/map";
import type { AppLang } from "./i18n";
import { langIdOf } from "./i18n";

export type AppConfig = {
  apiBaseUrl: string;
  basePath: string;
  defaultLang: AppLang;
  theme: Theme;
  publicMode: boolean;
  embedAllowedOrigins: string[];
  nominatimUrl: string | null;
  accent: string | null;
  mapCenter: [number, number] | null;
  mapZoom: number | null;
};

declare global {
  interface Window {
    __APP_CONFIG__?: unknown;
  }
}

export const defaultConfig: AppConfig = {
  apiBaseUrl: "/",
  basePath: "/",
  defaultLang: "hu",
  theme: "system",
  publicMode: true,
  embedAllowedOrigins: [],
  nominatimUrl: "https://nominatim.openstreetmap.org/search",
  accent: null,
  mapCenter: null,
  mapZoom: null
};

const langs = new Set<string>(["hu", "en"]);
const themes = new Set<string>(["light", "dark", "system"]);

function text(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
}

export function normaliseBasePath(value: unknown): string {
  const raw = text(value, "/");
  const withLeading = raw.startsWith("/") ? raw : `/${raw}`;

  return withLeading.length > 1 ? withLeading.replace(/\/+$/, "") : "/";
}

export function isOrigin(value: unknown): value is string {
  if (typeof value !== "string") return false;

  try {
    const url = new URL(value);

    return url.origin === value && (url.protocol === "http:" || url.protocol === "https:");
  } catch {
    return false;
  }
}

export function readConfig(raw: unknown): AppConfig {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const lang = source.defaultLang;
  const theme = source.theme;
  const origins = Array.isArray(source.embedAllowedOrigins) ? source.embedAllowedOrigins.filter(isOrigin) : [];
  const nominatim = source.nominatimUrl;

  return {
    apiBaseUrl: text(source.apiBaseUrl, defaultConfig.apiBaseUrl),
    basePath: normaliseBasePath(source.basePath),
    defaultLang: typeof lang === "string" && langs.has(lang) ? (lang as AppLang) : defaultConfig.defaultLang,
    theme: typeof theme === "string" && themes.has(theme) ? (theme as Theme) : defaultConfig.theme,
    publicMode: typeof source.publicMode === "boolean" ? source.publicMode : defaultConfig.publicMode,
    embedAllowedOrigins: origins,
    nominatimUrl: nominatim === null || nominatim === "" ? null : text(nominatim, defaultConfig.nominatimUrl ?? ""),
    accent: typeof source.accent === "string" && /^#[0-9a-fA-F]{6}$/.test(source.accent.trim()) ? source.accent.trim() : null,
    mapCenter: Array.isArray(source.mapCenter) && source.mapCenter.length === 2 && source.mapCenter.every(a => typeof a === "number" && Number.isFinite(a)) ? [source.mapCenter[0] as number, source.mapCenter[1] as number] : null,
    mapZoom: typeof source.mapZoom === "number" && Number.isFinite(source.mapZoom) ? source.mapZoom : null
  };
}

export function appConfig(): AppConfig {
  return readConfig(typeof window === "undefined" ? undefined : window.__APP_CONFIG__);
}

export function langFromSearch(search: string, fallback: AppLang): AppLang {
  const value = new URLSearchParams(search.startsWith("?") ? search.substring(1) : search).get("lang");

  return value !== null && langs.has(value) ? (value as AppLang) : fallback;
}

export function loginPath(returnUrl: string | null): string {
  return returnUrl && returnUrl !== "/" && !returnUrl.startsWith("/login") ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : "/login";
}

export type BuildApiOptions = {
  config: AppConfig;
  token: () => string | null;
  lang: AppLang;
  onUnauthorized: (error: ForteXApiError) => void;
  fetch?: typeof fetch;
};

export function buildApi(options: BuildApiOptions): ForteXApi {
  return new ForteXApi({
    baseUrl: options.config.apiBaseUrl,
    token: options.token,
    langId: langIdOf(options.lang),
    nominatimUrl: options.config.nominatimUrl,
    onUnauthorized: options.onUnauthorized,
    fetch: options.fetch
  });
}
